import { Card } from "@prisma/ui";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Layers, Package, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { PageHeader } from "../../components/PageHeader";
import { QueryState } from "../../components/QueryState";
import { RequestStatusChip } from "../../components/Status";
import { api, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatDate } from "../../lib/format";
import { useSession } from "../../lib/session";

type Row = components["schemas"]["ProjectRowOut"];
const STAGES = ["all", "strategy", "production", "review", "package"] as const;
type Stage = (typeof STAGES)[number];

/** Projects (decision U3): approved requests being worked on, grouped by stage. */
export function ProjectsPage() {
  const { t, i18n } = useTranslation();
  const me = useSession();
  const [params, setParams] = useSearchParams();
  const stage = (STAGES as readonly string[]).includes(params.get("stage") ?? "") ? (params.get("stage") as Stage) : "all";
  const search = params.get("q") || undefined;
  const [text, setText] = useState(search ?? "");
  const set = (key: "stage" | "q", value: string | null) => {
    const next = new URLSearchParams(params);
    if (value && !(key === "stage" && value === "all")) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };
  const q = useQuery({ queryKey: ["projects", stage, search],
    queryFn: () => call<Row[]>(api.GET("/app/v1/projects", { params: { query: { stage, q: search } } })) });
  return (
    <div className="space-y-6">
      <PageHeader titleKey="projects.title" subtitle={t("projects.subtitle")} />
      <div role="tablist" aria-label={t("projects.stages_label")} className="flex flex-wrap gap-1 border-b border-line">
        {STAGES.map((k) => (
          <button key={k} role="tab" type="button" aria-selected={stage === k}
            onClick={() => set("stage", k)}
            className="-mb-px border-b-2 border-transparent px-4 py-2.5 text-[14px] text-ink-2 hover:text-ink aria-selected:border-[var(--accent-primary)] aria-selected:font-semibold aria-selected:text-ink">
            {t(`projects.stage_${k}`)}
          </button>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); set("q", text.trim() || null); }}>
        <label className="relative inline-block">
          <span className="sr-only">{t("projects.search")}</span>
          <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-2" />
          <input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("projects.search")}
            className="h-10 w-72 rounded-[var(--radius-control)] border border-line bg-surface pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-2" />
        </label>
      </form>
      <QueryState query={q} emptyKey="projects.empty">
        {(rows) => (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {rows.map((r) => (
              <Link key={r.display_id} to={`/projects/${r.display_id}`}
                className="group block overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface shadow-[var(--shadow-card)] hover:border-[var(--border-strong)]">
                <div className="relative flex aspect-[16/9] items-center justify-center bg-elevated">
                  <div aria-hidden="true" className="absolute inset-0 bg-[image:var(--gradient-signature)] opacity-[0.10]" />
                  <Package aria-hidden="true" className="relative size-9 text-ink-2" strokeWidth={1.4} />
                </div>
                <Card className="space-y-2 rounded-none border-0 p-4 shadow-none">
                  <div className="text-[12px] text-ink-2">{r.display_id} · {t(`projects.stage_${r.stage}`)}</div>
                  <div className="truncate text-[16px] font-semibold text-ink">{r.product_name}</div>
                  <div className="truncate text-[13px] text-ink-2">{t(`requests.objective_${r.objective.toLowerCase()}`)}</div>
                  <RequestStatusChip status={r.status} />
                  <div className="flex items-center gap-2 text-[13px] text-ink-2">
                    <Layers aria-hidden="true" className="size-4" />
                    {r.deliverables.map((d) => t(`requests.deliverable_n_${d.type.toLowerCase()}`, { count: d.quantity })).join(" · ")}
                  </div>
                  <div className="flex items-center gap-2 text-[13px] text-ink-2">
                    <CalendarClock aria-hidden="true" className="size-4" />
                    {t("requests.due_on", { date: formatDate(r.deadline, i18n.language, me.time_zone) })}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </QueryState>
    </div>
  );
}
